import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChartBarIcon,
  Squares2X2Icon, 
  UsersIcon, 
  ArrowsRightLeftIcon,
  MapPinIcon, 
  BoltIcon as LightningBoltIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  UserGroupIcon,
  MegaphoneIcon,
  HomeIcon,
  UserCircleIcon,
  UserPlusIcon,
  ChartPieIcon,
  DocumentCheckIcon,
  BookmarkIcon
} from '@heroicons/react/24/outline';
import { Bar } from 'react-chartjs-2';

// Feature data array - easy to add/modify features
const FEATURE_DATA = [
  {
    id: 'planning-center',
    icon: ArrowsRightLeftIcon,
    title: "Auto-Sync with Planning Center",
    subtitle: "Real-time database updates",
    description: "Every conversation, every new member, every update flows seamlessly into your Planning Center database. No manual data entry. Ever.",
    benefits: [
      "Automatic profile creation for new members or followers",
      "Real-time Groups updates - new, updated, or archived groups will be reflected in app", 
      "Zero manual data entry required",
      "Instant group membership tracking - people added to groups in JoltChat will appear in groups in Planning Center"
    ],
    gradient: "from-blue-500 to-cyan-500",
    image: "/assets/images/joltchat-planning-center.png",
    categories: ["premium", "engagement"]
  },
  {
    id: 'group-management',
    icon: Squares2X2Icon,
    title: "Effortless Group Management",
    subtitle: "Connect people instantly",
    description: "Share group links, coordinate meetings, and build community with tools designed specifically for small group leaders.",
    benefits: [
      "One-click group link sharing",
      "Share group via QR code or link",
      "People who join groups are automatically added to Planning Center groups",
      "Integrated scheduling with Planning Center",
      "Location sharing and maps integration",
      "Separate group leaders chat within each group",
      "Take attendance and track engagement",
      "Ability to assign group roles to different leaders"
    ],
    gradient: "from-purple-500 to-pink-500",
    image: "/assets/images/joltchat-group-details-fwd.png",
    categories: ["communication"]
  },
  {
    id: 'groups-map',
    icon: MapPinIcon,
    title: "Interactive Groups Map",
    subtitle: "Find community on the map",
    description: "Your members can discover and join small groups through an intuitive, real-time map interface that makes connection effortless.",
    benefits: [
      "Live map with group locations",
      "View church information and events",
      "Direct group joining from map",
      "Church website integration",
      "Map only displays groups from your church",
      "Updated real-time as groups are created or updated on Planning Center",
    ],
    gradient: "from-green-500 to-emerald-500",
    image: "/assets/images/joltchat-map.png",
    categories: ["engagement", "social"]
  },
  {
    id: 'content-safety',
    icon: ShieldCheckIcon,
    title: "Explicit content moderation with AI",
    subtitle: "Keep Your Community Safe",
    description: "We leverage AI to filter explicit content and ensure your church community remains a safe space for all members.",
    benefits: [
      "Automated explicit content detection",
      "Real-time message moderation",
      "Safe environment for all ages",
      "Advanced safety reporting tools"
    ],
    gradient: "from-red-500 to-orange-500",
    categories: ["safety"]
  },
  {
    id: 'chat-features',
    icon: ChatBubbleLeftRightIcon,
    title: "Rich Messaging Experience",
    subtitle: "Modern messaging for your church",
    description: "Give your congregation the messaging experience they expect with rich text, media sharing, reactions and more.",
    benefits: [
      "Image and Video sharing",
      "Message reactions",
      "GIF library from Tenor",
      "Prayer Requests",
      "Edit or unsend messages",
    ],
    gradient: "from-rose-500 to-pink-500",
    image: "/assets/images/joltchat-group-msg.png",
    categories: ["communication", "social"]
  },
  {
    id: 'communities',
    icon: UserGroupIcon,
    title: "Communities",
    subtitle: "Create official spaces for people who share common interests",
    description: "Communities allow your members to connect around shared interests, hobbies, or life stages, fostering deeper relationships.",
    benefits: [
      "Create interest-based communities",
      "Communities are officially owned by your church",
      "Managed by your church leaders",
      "Members can join multiple communities",
      "Communities can be public or private",
      "New Community members will be automatically added to your Planning Center database",
    ],
    gradient: "from-indigo-500 to-blue-500",
    image: "/assets/images/joltchat-events.png",
    categories: ["premium", "communication", "engagement", "social"]
  },
  {
    id: 'announcements',
    icon: MegaphoneIcon,
    title: "Announcements",
    subtitle: "Keep your community informed - officially",
    description: "Announcements allow you to share important updates, events, and information with your members from your church.",
    benefits: [
      "Send official announcements to all your groups or communities",
      "Announcements appear in the chat with your church logo, name, and message",
      "Announcements will also send a push notification to all members",
      "Announcements can be sent by church leaders only",
      "Announcements can include images, links, and rich text",
    ],
    gradient: "from-yellow-500 to-amber-500",
    categories: ["premium", "communication", "engagement"]
  },
  {
    id: 'church-profile',
    icon: HomeIcon,
    title: "Church Profile",
    subtitle: "Your church as an easily discoverable profile",
    description: "Church profiles allow your people to find and follow your church, enhancing community engagement and visibility.",
    benefits: [
      "Profiles are synced with Planning Center",
      "People can follow your church to receive updates",
      "Your church website can be viewed directly from the profile",
      "All of your church's groups and communities are listed",
      "People can share your church profile with others via link or QR code",
    ],
    gradient: "from-cyan-500 to-sky-500",
    categories: ["premium", "engagement", "social"]
  },
  {
    id: 'follow-churches',
    icon: UserPlusIcon,
    title: "Follow your Church",
    subtitle: "Receive updates from your church even if you are not in a group or community.",
    description: "People can follow your church to receive updates, announcements, and events without needing to be in a group or community.",
    benefits: [
      "People who follow your church will be added to your Planning Center database",
      "Receive official announcements from your church",
      "Get updates on new groups, events, and communities",
      "Easy and low commitment way for people to stay connected",
      "People can follow your church without needing to join a group or community",
    ],
    gradient: "from-lime-500 to-green-500",
    categories: ["premium", "communication", "engagement"]
  },
  {
    id: 'user-profiles',
    icon: UserCircleIcon,
    title: "User Profiles",
    subtitle: "Users have a profile to connect with others",
    description: "User profiles allow church leaders and members to connect on a personal level, enhancing community engagement.",
    benefits: [
      "Profiles are synced with Planning Center",
      "Connect with other members through profiles",
      "Add up to three profile photos",
      "Share a small bio",
      "Share your home church",
      "Stay engaged with your church community",
    ],
    gradient: "from-blue-500 to-indigo-500",
    categories: ["social"]
  },
  {
    id: 'dashboard',
    icon: ChartPieIcon,
    title: "JoltChat Dashboard",
    subtitle: "Overview of your church's engagement",
    description: "JoltChat integration provides a dashboard with a comprehensive view of your church's people data and engagement metrics, helping you understand your community better.",
    benefits: [
      "View total number of people in your groups and communities",
      "View people data such as phone numbers, emails, and more",
      "Give users admin permissions",
      "View group and community engagement metrics"
    ],
    gradient: "from-fuchsia-500 to-purple-500",
    categories: ["premium", "engagement"]
  },
  {
    id: 'events',
    icon: CalendarIcon,
    title: "Events (coming soon)",
    subtitle: "Create and manage events for your church",
    description: "JoltChat's event management feature will allow you to create, manage, and promote events within your church community, making it easier for members to stay connected and engaged.",
    benefits: [
      "Create events directly in JoltChat",
      "Events will be synced with Planning Center",
      "Members can RSVP to events",
      "Event details will be shared in the app",
      "Event reminders will be sent to attendees",
      "Event attendance will be tracked",
      "Event metrics will be available in the JoltChat dashboard",
      "Events can be shared via link or QR code",
      "Events can be public or private",
      "Events can be shared in groups or communities"
    ],
    gradient: "from-gray-500 to-gray-700",
    categories: ["coming-soon"]
  },
  {
    id: 'automated-attendance',
    icon: ChartBarIcon,
    title: "Automated Attendance (coming soon)",
    subtitle: "Automated location based attendance for groups and events",
    description: "Forget the hassle of manual attendance tracking. JoltChat's automated attendance feature uses location data to track who attended your groups and events.",
    benefits: [
      "Automatic attendance tracking based on location",
      "No more manual check-ins or roll calls",
      "Attendance data will be sent in a weekly email to group leaders and pastors",
      "Attendance metrics will be available in the JoltChat dashboard",
    ],
    gradient: "from-gray-500 to-gray-700",
    categories: ["coming-soon"]
  },
  {
    id: 'automated-reminders',
    icon: BookmarkIcon,
    title: "Automated Reminders (coming soon)",
    subtitle: "Automatically remind users about upcoming groups and events",
    description: "JoltChat's automated reminders feature will keep your community informed and engaged without having to rely on manual follow-ups.",
    benefits: [
      "Automatic reminders for upcoming groups and events",
      "No more manual follow-ups",
      "Reminder notifications will be sent via email and push notifications",
      "Customizable reminder settings for each group or event",
    ],
    gradient: "from-gray-500 to-gray-700",
    categories: ["coming-soon"]
  }
];

// Stats data - easy to modify
const STATS_DATA = [
  { number: "100%", label: "Planning Center Integration", icon: CircleStackIcon },
  { number: "0%", label: "Manual Data Entry", icon: CheckCircleIcon },
  { number: "∞", label: "Growth Potential", icon: SparklesIcon }
];

// Categories for filtering
const CATEGORIES = [
  { id: 'all', label: 'All Features' },
  { id: 'premium', label: 'Premium Features' },
  { id: 'communication', label: 'Communication' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'social', label: 'Social' },
  { id: 'coming-soon', label: 'Coming Soon' }
];

const Features = () => {
  // State management
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredFeatures, setFilteredFeatures] = useState(FEATURE_DATA);
  
  // Refs for scroll animations
  const heroRef = useRef(null);
  const featuresSectionRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const isFeaturesInView = useInView(featuresSectionRef, { once: true });

  // Filter features when category changes
  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredFeatures(FEATURE_DATA);
    } else {
      setFilteredFeatures(FEATURE_DATA.filter(feature => 
        feature.categories && feature.categories.includes(activeCategory)
      ));
    }
  }, [activeCategory]);

  // Animation variants
  const featureCardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({ 
      opacity: 1, 
      y: 0,
      transition: { delay: 0.1 * index, duration: 0.5 }
    })
  };

  const handleFeatureToggle = useCallback((featureId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  }, []);

  // Memoized FeatureCard component to prevent unnecessary re-renders
  const FeatureCard = React.memo(({ feature, index, isExpanded, onToggle }) => (
    <div
      className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
        isExpanded 
          ? 'bg-gradient-to-r from-white/20 to-white/10 border-2 border-white/30 shadow-xl' 
          : 'bg-white/5 border border-white/10 hover:bg-white/10'
      }`}
      onClick={() => onToggle(feature.id)}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${feature.gradient}`}>
          <feature.icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
            <span className="text-gray-400">
              {isExpanded ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
            </span>
          </div>
          <p className="text-sm text-gray-400 mb-3">{feature.subtitle}</p>
          
          {/* Benefits preview - only visible when collapsed */}
          {!isExpanded && (
            <div className="flex flex-wrap gap-2 mb-3">
              {feature.benefits.slice(0, 2).map((benefit, i) => (
                <span key={i} className="inline-flex items-center text-xs bg-white/10 rounded-full px-3 py-1">
                  <CheckCircleIcon className="w-3 h-3 text-green-400 mr-1" />
                  {benefit.length > 30 ? `${benefit.substring(0, 30)}...` : benefit}
                </span>
              ))}
              {feature.benefits.length > 2 && (
                <span className="inline-flex items-center text-xs bg-white/10 rounded-full px-3 py-1 text-gray-400">
                  +{feature.benefits.length - 2} more
                </span>
              )}
            </div>
          )}

          {/* Expanded content */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="pt-2">
              <p className="text-gray-300 mb-4">{feature.description}</p>
              
              <div className="flex flex-col md:flex-row gap-6 mt-4">
                {/* Benefits list - left side */}
                <div className="md:w-1/2">
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, bIndex) => (
                      <li
                        key={bIndex}
                        className="flex items-center gap-2 text-sm text-gray-400"
                      >
                        <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Feature image - right side */}
                {feature.image && (
                  <div className="md:w-1/2">
                    <div className="relative">
                      <img 
                        src={feature.image} 
                        alt={feature.title} 
                        className="relative rounded-lg w-full h-auto object-contain border border-white/10"
                        style={{ maxHeight: "280px", objectPosition: "center" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Highlight effect for expanded feature */}
      {isExpanded && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl -z-10"
        />
      )}
    </div>
  ));

  // Feature List Component
  const FeatureList = React.memo(() => (
    <div className="space-y-6 max-w-4xl mx-auto">
      {filteredFeatures.map((feature, index) => (
        <FeatureCard
          key={feature.id}
          feature={feature}
          index={index}
          isExpanded={!!expandedFeatures[feature.id]}
          onToggle={handleFeatureToggle}
        />
      ))}
    </div>
  ));

  // Category Tabs Component
  const CategoryTabs = () => (
    <div className="flex flex-wrap justify-center gap-3 mb-12">
      {CATEGORIES.map((category) => (
        <motion.button
          key={category.id}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            activeCategory === category.id
              ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/30 text-white border-2 border-white/30'
              : 'bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10'
          }`}
          onClick={() => {
            setActiveCategory(category.id);
            setExpandedFeatures({}); // Reset expanded state when category changes
          }}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.95 }}
        >
          {category.label}
        </motion.button>
      ))}
    </div>
  );

  // No need for FeaturedShowcase since we'll display all features in a list

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 min-h-screen">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 animate-pulse"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl animate-bounce"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl animate-bounce delay-1000"></div>
      </div>

      {/* Hero Section */}
      <section ref={heroRef} className="relative z-10 pt-40 pb-16">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >

            {/* Main heading */}
            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Powerful Features in <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}>JoltChat</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              The <strong className="text-white">only messaging platform</strong> designed exclusively for churches that 
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-semibold"> automatically syncs with Planning Center</span>. 
              Transform how your small groups connect, grow, and thrive.
            </motion.p>

            {/* Stats Row */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {STATS_DATA.map((stat, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 text-center hover:bg-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -5 }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.7 + index * 0.1 }}
                >
                  <stat.icon className="w-8 h-8 mx-auto mb-3 text-blue-400" />
                  <div className={`font-bold text-white mb-2 ${index === 2 ? 'text-6xl' : 'text-3xl'}`}>{stat.number}</div>
                  <div className="text-gray-300">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Interactive Features Section */}
      <section ref={featuresSectionRef} className="relative z-10 py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isFeaturesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Explore All <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}>Features</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Every feature designed to eliminate friction and amplify connection in your church community.
            </p>
            
            {/* Category filter tabs */}
            <CategoryTabs />
          </motion.div>

          {/* Features list */}
          <FeatureList />
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative z-10 py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg border border-white/20 rounded-3xl p-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isFeaturesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={isFeaturesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Ready to Transform Your Church Community?
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-300 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={isFeaturesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1 }}
            >
              Join churches already experiencing the JoltChat difference. Setup takes less than 5 minutes.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={isFeaturesInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <Link 
                to="/sign-in" 
                className="group relative px-8 py-4 text-white font-bold text-lg rounded-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
                style={{ background: 'linear-gradient(to right, rgb(244, 54, 54), rgb(244, 111, 54))' }}
              >
                <span className="flex items-center gap-2">
                  Start Today
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Features;